/**
 * Actions y hooks para Auditoría de Ventas Home
 */

import { useQuery } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AuditoriaVentasHomeDetalle,
  AuditoriaFiltros,
  AuditoriaPaginatedResponse,
  AuditoriaEstadisticas,
  SelectOption,
  UsuarioAuditoria,
  TrazabilidadEtapaGrupo,
  EtapaVenta,
  ETAPAS_VENTA_ORDEN,
} from '@/shared/interfaces/erp/auditoria-ventas-home.interface';

const { get } = erpApi();
const BASE_URL = `${urlBackend}api/v1/bitacora-ventas`;

/**
 * Obtiene lista paginada de auditoría
 */
export const useFetchAuditoriaVentasHome = (filtros: AuditoriaFiltros) => {
  return useQuery<AuditoriaPaginatedResponse>({
    queryKey: ['auditoria-ventas-home', filtros],
    queryFn: async () => {
      const params = new URLSearchParams();

      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await get<AuditoriaPaginatedResponse>(`${BASE_URL}/?${params.toString()}`, true);
      return response.data ?? (response as unknown as AuditoriaPaginatedResponse);
    },
    staleTime: 30000,
  });
};

/**
 * Obtiene detalle de un registro de auditoría
 */
export const useFetchAuditoriaDetalle = (id: string, enabled: boolean = true) => {
  return useQuery<AuditoriaVentasHomeDetalle>({
    queryKey: ['auditoria-ventas-home', 'detalle', id],
    queryFn: async () => {
      const response = await get<AuditoriaVentasHomeDetalle>(`${BASE_URL}/${id}/`, true);
      return response.data ?? (response as unknown as AuditoriaVentasHomeDetalle);
    },
    enabled: !!id && enabled,
    staleTime: 60000,
  });
};

/**
 * Obtiene estadísticas de auditoría
 */
export const useFetchAuditoriaEstadisticas = (dias: number = 30) => {
  return useQuery<AuditoriaEstadisticas>({
    queryKey: ['auditoria-ventas-home', 'estadisticas', dias],
    queryFn: async () => {
      const response = await get<AuditoriaEstadisticas>(`${BASE_URL}/estadisticas/?dias=${dias}`, true);
      return response.data ?? (response as unknown as AuditoriaEstadisticas);
    },
    staleTime: 60000,
  });
};

/**
 * Obtiene auditoría de un registro específico
 */
export const useFetchAuditoriaPorRegistro = (registroId: string, registroTipo?: string) => {
  return useQuery<AuditoriaVentasHomeDetalle[]>({
    queryKey: ['auditoria-ventas-home', 'por-registro', registroId, registroTipo],
    queryFn: async () => {
      const params = new URLSearchParams({ registro_id: registroId });
      if (registroTipo) {
        params.append('registro_tipo', registroTipo);
      }
      const response = await get<any>(`${BASE_URL}/por_registro/?${params.toString()}`, true);
      const data = response.data ?? response;
      return (data as any).results || data;
    },
    enabled: !!registroId,
    staleTime: 30000,
  });
};

/**
 * Obtiene lista de módulos
 */
export const useFetchModulos = () => {
  return useQuery<SelectOption[]>({
    queryKey: ['auditoria-ventas-home', 'modulos'],
    queryFn: async () => {
      const response = await get<SelectOption[]>(`${BASE_URL}/modulos/`, true);
      return response.data ?? (response as unknown as SelectOption[]);
    },
    refetchOnWindowFocus: false,
    staleTime: 300000, // 5 minutos
  });
};

/**
 * Obtiene lista de acciones
 */
export const useFetchAcciones = () => {
  return useQuery<SelectOption[]>({
    queryKey: ['auditoria-ventas-home', 'acciones'],
    queryFn: async () => {
      const response = await get<SelectOption[]>(`${BASE_URL}/acciones/`, true);
      return response.data ?? (response as unknown as SelectOption[]);
    },
    staleTime: 300000, // 5 minutos
  });
};

/**
 * Obtiene lista de usuarios con acciones
 */
export const useFetchUsuariosAuditoria = () => {
  return useQuery<UsuarioAuditoria[]>({
    queryKey: ['auditoria-ventas-home', 'usuarios'],
    queryFn: async () => {
      const response = await get<UsuarioAuditoria[]>(`${BASE_URL}/usuarios/`, true);
      return response.data ?? (response as unknown as UsuarioAuditoria[]);
    },
    staleTime: 60000,
  });
};

/**
 * Formatea fecha de log
 */
export const formatFechaAuditoria = (fecha: string | null): string => {
  if (!fecha) return '-';
  try {
    const date = new Date(fecha);
    return date.toLocaleString('es-EC', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return fecha;
  }
};

/**
 * Obtiene color de acción
 */
export const getAccionColor = (accion: string): 'primary' | 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const colors: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    CREAR: 'success',
    ACTUALIZAR: 'info',
    ELIMINAR: 'error',
    APROBAR: 'success',
    RECHAZAR: 'error',
    ACEPTAR: 'success',
    FIRMAR: 'primary',
    CARGAR_DOCUMENTO: 'info',
    CAMBIAR_ESTADO: 'warning',
    ASIGNAR: 'primary',
    REASIGNAR: 'warning',
    CANCELAR: 'error',
    CONSULTAR: 'default',
  };
  return colors[accion] || 'default';
};

/**
 * Obtiene color de módulo
 */
export const getModuloColor = (modulo: string): 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary' => {
  const colors: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary'> = {
    PROSPECTO: 'primary',
    PREVENTA: 'info',
    AGENDAMIENTO: 'warning',
    CLIENTE: 'success',
    CONTRATO: 'secondary',
    ORDEN_TRABAJO: 'error',
  };
  return colors[modulo] || 'primary';
};

/**
 * Obtiene color de método HTTP
 */
export const getMetodoHttpColor = (metodo: string): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
  const colors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'> = {
    GET: 'info',
    POST: 'success',
    PUT: 'warning',
    PATCH: 'warning',
    DELETE: 'error',
  };
  return colors[metodo?.toUpperCase()] || 'default';
};

// ========================================
// Trazabilidad
// ========================================

/**
 * Obtiene todos los logs de trazabilidad para un prospecto (cross-módulo).
 * Usa el endpoint /trazabilidad/ que busca en todos los módulos relacionados
 * (Prospecto → Preventa → Preagendamiento → Agendamiento) y devuelve
 * detalle completo incluyendo cambios.
 */
export const useFetchTrazabilidadProspecto = (prospectoId: string, enabled = true) => {
  return useQuery<AuditoriaVentasHomeDetalle[]>({
    queryKey: ['auditoria-ventas-home', 'trazabilidad', prospectoId],
    queryFn: async () => {
      const response = await get<any>(`${BASE_URL}/trazabilidad/?prospecto_id=${prospectoId}`, true);
      const data = response.data ?? response;
      return Array.isArray(data) ? data : (data as any).results || [];
    },
    enabled: !!prospectoId && enabled,
    staleTime: 30000,
  });
};

/**
 * Obtiene lista de prospectos (eventos CREAR en PROSPECTO) para la vista de trazabilidad
 */
export const useFetchTrazabilidadProspectos = (filtros: AuditoriaFiltros) => {
  return useQuery<AuditoriaPaginatedResponse>({
    queryKey: ['auditoria-ventas-home', 'trazabilidad-lista', filtros],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('modulo', 'PROSPECTO');
      params.append('accion', 'CREAR');

      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && key !== 'modulo' && key !== 'accion') {
          params.append(key, String(value));
        }
      });

      const response = await get<AuditoriaPaginatedResponse>(`${BASE_URL}/?${params.toString()}`, true);
      return response.data ?? (response as unknown as AuditoriaPaginatedResponse);
    },
    staleTime: 30000,
  });
};

/**
 * Agrupa logs por etapa del flujo de ventas
 */
export const agruparLogsPorEtapa = (logs: AuditoriaVentasHomeDetalle[]): TrazabilidadEtapaGrupo[] => {
  const moduloToEtapa: Record<string, EtapaVenta> = {
    PROSPECTO: 'PROSPECTO',
    PREVENTA: 'PREVENTA',
    PREAGENDAMIENTO: 'PREAGENDAMIENTO',
    AGENDAMIENTO: 'AGENDAMIENTO',
  };

  const grupos: Record<EtapaVenta, AuditoriaVentasHomeDetalle[]> = {
    PROSPECTO: [],
    PREVENTA: [],
    PREAGENDAMIENTO: [],
    AGENDAMIENTO: [],
  };

  logs.forEach((log) => {
    const etapa = moduloToEtapa[log.modulo];
    if (etapa) {
      grupos[etapa].push(log);
    }
  });

  return ETAPAS_VENTA_ORDEN
    .map((etapa) => {
      const eventos = grupos[etapa].sort(
        (a, b) => new Date(a.fecha_hora).getTime() - new Date(b.fecha_hora).getTime()
      );
      const ultimoEvento = eventos[eventos.length - 1];

      let estadoFinal: string | null = null;
      if (ultimoEvento?.cambios?.estado_prospecto) {
        estadoFinal = String(ultimoEvento.cambios.estado_prospecto.nuevo);
      } else if (ultimoEvento?.cambios?.estado_preventa) {
        estadoFinal = String(ultimoEvento.cambios.estado_preventa.nuevo);
      } else if (ultimoEvento?.cambios?.estado_preagendamiento) {
        estadoFinal = String(ultimoEvento.cambios.estado_preagendamiento.nuevo);
      } else if (ultimoEvento?.cambios?.estado_agendamiento) {
        estadoFinal = String(ultimoEvento.cambios.estado_agendamiento.nuevo);
      } else if (ultimoEvento?.cambios?.estado) {
        estadoFinal = String(ultimoEvento.cambios.estado.nuevo);
      }

      return {
        etapa,
        eventos,
        estado_final: estadoFinal,
        fecha_inicio: eventos[0]?.fecha_hora ?? null,
        fecha_fin: ultimoEvento?.fecha_hora ?? null,
      };
    })
    .filter((g) => g.eventos.length > 0);
};

/**
 * Color por etapa de venta
 */
export const getEtapaColor = (etapa: EtapaVenta): string => {
  const colors: Record<EtapaVenta, string> = {
    PROSPECTO: '#1976d2',
    PREVENTA: '#0288d1',
    PREAGENDAMIENTO: '#ed6c02',
    AGENDAMIENTO: '#2e7d32',
  };
  return colors[etapa] || '#757575';
};
